const libFableServiceProviderBase = require('fable-serviceproviderbase');

/**
 * Headlight Stationing fable service
 */
class HeadlightStationing extends libFableServiceProviderBase
{
	/**
	 * @param {import('fable')} pFable - The fable instance
	 * @param {any} [pOptions] - The options for the service
	 * @param {any} [pServiceHash] - The hash for the service instance
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		this.serviceType = 'HeadLightStationing';
	}

	/**
	 * convert a "station" value to a number
	 * EX: "125+00" to "12500"
	 * @param {string|number} pValue
	 * @return {number} the numeric value of the station and offset
	 * @memberof HeadlightStationing
	 * @example
	 * const tmpValue = '125+00';
	 * const tmpNumberValue = tmpHeadlightStationing.fromStationValue(tmpValue);
	 * // tmpNumberValue = 12500
	*/
	fromStationValue(pValue)
	{
		// we expect a string here regardless of what's passed
		const tmpValue = typeof (pValue) === 'undefined' ? '' : pValue.toString();

		// if they included the plus sign manually, or it's an updated 'pValue'
		if (tmpValue.indexOf('+') !== -1)
		{
			// honor the first +, ignore any subsequent ones, strip spaces, then do our best
			const tmpDescendingValues = tmpValue.split(/\s*\+\s*/);

			let tmpComputed = 0;
			for (let i = 0; i < tmpDescendingValues.length; i++)
			{
				tmpComputed += Math.pow(100, 1 - i) * (parseFloat(tmpDescendingValues[i]) || 0);
			}
			return tmpComputed;
		}

		const tmpDecimalIndex = tmpValue.indexOf('.');
		if (tmpDecimalIndex === -1 && tmpValue.trim().length < 3)
		{
			return parseFloat(tmpValue) * 100; // two digits or fewer is treated as a station value (I hate this)
		}
		return parseFloat(tmpValue);
	}

	/**
	 * Normalize a value that may be EITHER a station-formatted string ("0+50")
	 * OR an already-numeric station offset (50) into a numeric offset in feet.
	 *
	 * Unlike fromStationValue(), this does NOT apply the "a bare two-digit number
	 * means N+00" (x100) heuristic. A value without a '+' is treated as a literal
	 * offset, so an offset of 50 stays 50 (it does not become 5000 / "50+00").
	 *
	 * This is what consumers need when the value has already been reduced to a
	 * numeric offset (e.g. the Station input provider stores the numeric offset
	 * as its bound value), while still tolerating legacy rows that persisted the
	 * station-formatted string.
	 *
	 * @param {string|number} pValue
	 * @return {number} the numeric station offset, or NaN if unparseable
	 * @memberof HeadlightStationing
	 * @example
	 * tmpHeadlightStationing.normalizeStationOffset(50);      // 50
	 * tmpHeadlightStationing.normalizeStationOffset('0+50');  // 50
	 * tmpHeadlightStationing.normalizeStationOffset('1+50');  // 150
	*/
	normalizeStationOffset(pValue)
	{
		if (typeof pValue === 'number')
		{
			return pValue;
		}
		const tmpValue = (typeof pValue === 'undefined' || pValue === null) ? '' : pValue.toString();
		// A '+' means it's a station-formatted string ("0+50"); parse it the normal way.
		if (tmpValue.indexOf('+') !== -1)
		{
			return this.fromStationValue(tmpValue);
		}
		// Otherwise it is already a literal numeric offset in feet - do not scale it.
		return parseFloat(tmpValue);
	}

	/**
	 * convert a number string to a "station" value
	 * EX: "12500" to "125+00"
	 * @param {any} pValue - the numeric offset
	 * @return {string} the converted station and offset
	 * @memberof HeadlightStationing
	 * @example
	 * const tmpValue = 12500;
	 * const tmpStationValue = tmpHeadlightStationing.toStationValue(tmpValue);
	 * // tmpStationValue = '125+00'
	*/
	toStationValue(pValue)
	{
		/** @type {number|string} */
		let tmpValue;
		switch (typeof pValue)
		{
			case 'string':
				tmpValue = pValue.includes('+') ? this.fromStationValue(pValue) : parseFloat(pValue);
				break;
			case 'number':
				tmpValue = pValue;
				break;
			default:
				tmpValue = String(pValue);
		}
		let returnValue = '';

		// if they pass a non-parseable value, return it as is early
		if (isNaN(Number(tmpValue)))
		{
			console.error('Invalid station value: ' + pValue);
			return pValue;
		}
		tmpValue = tmpValue.toString();

		// we have to account for decimals too
		const [ whole, fractional ] = tmpValue.split('.');

		// insert the '+' before the last two digits before the decimal
		if (whole.length === 2)
		{
			returnValue = '0+' + whole;
		}
		else if (whole.length === 1)
		{
			returnValue = '0+0' + whole;
		}
		else if (whole.length)
		{
			returnValue = whole.slice(0, whole.length - 2) + '+' + whole.slice(whole.length - 2);
		}
		else
		{
			this.fable.log.error('Invalid station value: ' + pValue);
			// return the original value, ignore the decimal and formatting
			return pValue;
		}
		// if there's a decimal, add it
		if (fractional)
		{
			returnValue += '.' + fractional;
		}

		return returnValue;
	}

	/**
	 * add two station values together
	 *
	 * @param {string|number} pValue1
	 * @param {string|number} pValue2
	 *
	 * @return {string} the sum of the two station values, formatted as a station value
	 * @memberof HeadlightStationing
	 * @example
	 * const tmpValue1 = '125+00';
	 * const tmpValue2 = '125+00';
	 * const tmpNumberValue = tmpHeadlightStationing.addStationValues(tmpValue1, tmpValue2);
	 * // tmpNumberValue = '250+00'
	 * @example
	 * const tmpValue1 = '125+00';
	 * const tmpValue2 = 'potato';
	 * const tmpNumberValue = tmpHeadlightStationing.addStationValues(tmpValue1, tmpValue2);
	 * // tmpNumberValue = 'NaN'
	*/
	addStationValues(pValue1, pValue2)
	{
		return this.toStationValue(this.fromStationValue(pValue1) + this.fromStationValue(pValue2));
	}

	/**
	 * subtract two station values
	 *
	 * @param {string|number} pValue1
	 * @param {string|number} pValue2
	 *
	 * @return {string} the difference of the two station values, formatted as a station value
	 * @memberof HeadlightStationing
	 * @example
	 * const tmpValue1 = '125+00';
	 * const tmpValue2 = '125+00';
	 * const tmpNumberValue = tmpHeadlightStationing.subtractStationValues(tmpValue1, tmpValue2);
	 * // tmpNumberValue = '0+00'
	 * @example
	 * const tmpValue1 = '125+00';
	 * const tmpValue2 = 'potato';
	 * const tmpNumberValue = tmpHeadlightStationing.subtractStationValues(tmpValue1, tmpValue2);
	 * // tmpNumberValue = 'NaN'
	*/
	subtractStationValues(pValue1, pValue2)
	{
		return this.toStationValue(this.fromStationValue(pValue1) - this.fromStationValue(pValue2));
	}
}

module.exports = HeadlightStationing;
